import { useState } from 'react';
import { ButtonRounded } from '../../components/UISystem/Buttons';
import { SHADOWS } from '../../styles/UISystem';
import { VERSION } from '../../version';
import { isValidProfileUserId } from '../../utils/identityUtils';
import { clearStoredTier } from '../../utils/userTier';

/**
 * The row under the code: its status, plus the build number and the code's
 * link on a test site. Lifted from the old code screen unchanged, so the new
 * code page shows the same readout (Mark, 2026-10-21: "which build am I on").
 * Drawn under a positioned parent (`top: 100%`), as on the old screen.
 *
 * @param {Object} p
 * @param {boolean} p.ready - the code can be scanned (channel + session + profile)
 * @param {boolean} p.stuck - not ready after 10 s: "Tap to refresh"
 * @param {boolean} p.showDev - a test site: the build number, Ready and the link
 * @param {string|null} p.profileUserId
 * @param {() => void} p.onEditId - the link opens the edit-code tool
 */
export const CodeStatusRow = ({ ready, stuck, showDev, profileUserId, onEditId }) => (
  <>
    {/* Connection status indicator (all branches):
        - Not ready + <10s: "Connecting..." (yellow) — normal warmup
        - Not ready + >10s: "Tap to refresh" (yellow, tappable) — stuck
        - Fully ready + main: hidden — nothing to say
        - Fully ready + dev: "Ready" (green) + profileUserId link */}
    {!ready && (
      <div
        onClick={stuck ? () => window.location.reload() : undefined}
        style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 14px',
          borderRadius: '16px',
          backgroundColor: 'rgba(251, 191, 36, 0.1)',
          cursor: stuck ? 'pointer' : 'default',
        }}
      >
        <div
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: stuck ? '#f97316' : '#f59e0b',
          }}
        />
        <span
          style={{
            fontSize: '14px',
            color: stuck ? '#f97316' : '#f59e0b',
            fontWeight: 500,
          }}
        >
          {stuck ? 'Tap to refresh' : 'Connecting...'}
        </span>
      </div>
    )}
    {ready && showDev && (
      <div
        style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <span
          style={{
            fontSize: '12px',
            color: '#aaa',
            fontFamily: 'monospace',
          }}
        >
          {VERSION}
        </span>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 14px',
            borderRadius: '16px',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
          }}
        >
          <div
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#22c55e',
            }}
          />
          <span
            style={{
              fontSize: '14px',
              color: '#22c55e',
              fontWeight: 500,
            }}
          >
            Ready
          </span>
        </div>
        <span
          onClick={onEditId}
          style={{
            fontSize: '14px',
            color: '#888',
            fontFamily: 'monospace',
            cursor: 'pointer',
          }}
        >
          {profileUserId ? `/u/${profileUserId}` : '------'}
        </span>
      </div>
    )}
  </>
);

/**
 * The edit-code tool (test sites; a dev tool for QR test images): a new
 * profile id, saved to this device, then a reload.
 */
export const EditProfileIdModal = ({ initialId, onClose }) => {
  const [value, setValue] = useState(initialId || '');
  const [error, setError] = useState('');
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          width: '85%',
          maxWidth: '360px',
          boxShadow: SHADOWS.TIER4,
        }}
      >
        <h3
          style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1C1C1E',
            marginTop: 0,
            marginBottom: '8px',
          }}
        >
          Edit Profile ID
        </h3>
        <p
          style={{
            fontSize: '14px',
            color: '#8E8E93',
            marginBottom: '16px',
          }}
        >
          When creating custom QRs
        </p>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError('');
          }}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            fontFamily: 'monospace',
            border: `1px solid ${error ? '#FF3B30' : '#E5E5E5'}`,
            borderRadius: '10px',
            marginBottom: '4px',
            boxSizing: 'border-box',
          }}
        />
        {error && (
          <p
            style={{
              fontSize: '12px',
              color: '#FF3B30',
              margin: '0 0 4px 0',
            }}
          >
            {error}
          </p>
        )}
        <p
          style={{
            fontSize: '12px',
            color: '#8E8E93',
            marginBottom: '20px',
            wordBreak: 'break-all',
          }}
        >
          /u/{value || '...'}
        </p>
        <div
          style={{
            display: 'flex',
            gap: '12px',
          }}
        >
          <ButtonRounded
            text="Cancel"
            variant="secondary"
            size="auto"
            onClick={() => onClose()}
            style={{ flex: 1 }}
          />
          <ButtonRounded
            text="Save"
            variant="blue"
            size="auto"
            onClick={() => {
              const trimmed = value.trim();
              if (!trimmed) return;
              if (!isValidProfileUserId(trimmed)) {
                setError('4-12 chars, letters/numbers/dash/underscore only');
                return;
              }
              localStorage.setItem('profile_user_id', trimmed);
              clearStoredTier(); // a different profile: reseed its tier on open
              onClose();
              window.location.reload();
            }}
            style={{ flex: 1 }}
          />
        </div>
      </div>
    </div>
  );
};
